import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useCart } from '../context/CartContext';

const SIZES = ['S', 'M', 'L', 'XL'];
const PLACEHOLDER_LG = 'https://placehold.co/600x600/f4f4f4/888888?text=PORK+CO.';
const PLACEHOLDER_SM = 'https://placehold.co/64x64/f4f4f4/888888?text=PC';

// Shared styles for the +/− quantity control used here and in Cart
export const qtyBtnStyle = {
  width: '40px',
  height: '40px',
  border: '1px solid #ddd',
  background: 'white',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none'
};

const arrowBtnStyle = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.9)',
  border: '1px solid #e0e0e0',
  borderRadius: '2px',
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#111',
  zIndex: 2,
  lineHeight: 1,
  userSelect: 'none',
  transition: 'background 0.15s ease'
};

// ── Stock helpers ────────────────────────────────────────────────────────────

// Stock available for a specific size (or the flat stock for no-size products)
function getSizeStock(product, size) {
  if (product.hasSize) {
    return typeof product.stock === 'object' && product.stock !== null
      ? (product.stock[size] ?? 0)
      : 0;
  }
  return typeof product.stock === 'number' ? product.stock : 0;
}

// Is ANY stock available?
function isAvailable(product) {
  if (product.hasSize) {
    if (typeof product.stock !== 'object' || product.stock === null) return false;
    return Object.values(product.stock).some(v => v > 0);
  }
  return getSizeStock(product, null) > 0;
}

// ────────────────────────────────────────────────────────────────────────────

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity]         = useState(1);
  const [added, setAdded]               = useState(false);
  const [sizeError, setSizeError]       = useState(false);

  // Carousel
  const [activeIdx, setActiveIdx] = useState(0);
  const touchStartX = useRef(null);

  // Zoom — click to toggle, cursor pans while zoomed
  const [isZoomed, setIsZoomed] = useState(false);
  const zoomImgRef = useRef(null);

  useEffect(() => {
    async function loadProduct() {
      try {
        const snap = await getDoc(doc(db, 'products', id));
        if (snap.exists()) {
          setProduct({ id: snap.id, ...snap.data() });
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setNotFound(true);
      }
      setLoading(false);
    }
    loadProduct();
  }, [id]);

  // Reset carousel + zoom when product changes
  useEffect(() => { setActiveIdx(0); setIsZoomed(false); setSelectedSize(null); }, [id]);

  function handleSizeSelect(size) {
    setSelectedSize(size);
    setSizeError(false);
  }

  function handleAddToCart() {
    if (product.hasSize && !selectedSize) {
      setSizeError(true);
      return;
    }
    addToCart(product, product.hasSize ? selectedSize : null, null, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  }

  // Overall stock label (for header badge)
  function stockLabel(product) {
    if (!isAvailable(product)) return { text: 'Out of Stock', color: '#888' };
    if (product.hasSize) {
      const total = typeof product.stock === 'object'
        ? Object.values(product.stock).reduce((a, b) => a + (b || 0), 0)
        : 0;
      if (total <= 5) return { text: `Only ${total} left`, color: '#c84b11' };
      return { text: `${total} in stock`, color: '#2e7d32' };
    }
    const s = getSizeStock(product, null);
    if (s === 0) return { text: 'Out of Stock', color: '#888' };
    if (s <= 5)  return { text: `Only ${s} left`, color: '#c84b11' };
    return { text: `${s} in stock`, color: '#2e7d32' };
  }

  // Zoom — update transform-origin directly on the img ref (no re-renders while panning)
  function handleMouseMove(e) {
    if (!isZoomed || !zoomImgRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left)  / rect.width)  * 100;
    const y = ((e.clientY - rect.top)   / rect.height) * 100;
    zoomImgRef.current.style.transformOrigin = `${x}% ${y}%`;
  }

  // Touch swipe handlers for mobile
  function handleTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
  }
  function handleTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) goNext(); else goPrev();
  }

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border spinner-border-sm" style={{ color: '#c84b11' }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="container py-5 text-center">
        <h4 className="fw-bold text-uppercase mb-2" style={{ letterSpacing: '0.08em' }}>Product Not Found</h4>
        <p className="text-muted mb-4">That product doesn't exist or was removed.</p>
        <Link to="/products" className="btn btn-piggy">Back to Shop</Link>
      </div>
    );
  }

  const images = product.images?.length
    ? product.images
    : (product.imageUrl ? [product.imageUrl] : []);

  function goPrev() { setIsZoomed(false); setActiveIdx(i => (i - 1 + images.length) % images.length); }
  function goNext() { setIsZoomed(false); setActiveIdx(i => (i + 1) % images.length); }

  const stockInfo    = stockLabel(product);
  const isOutOfStock = !isAvailable(product);
  // Max qty depends on whether a size is selected
  const maxQty = product.hasSize
    ? (selectedSize ? getSizeStock(product, selectedSize) : 99)
    : getSizeStock(product, null);

  return (
    <div className="container py-5">

      <button
        className="btn btn-link p-0 mb-4 text-decoration-none"
        style={{ color: '#888', fontSize: '0.9rem' }}
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      <div className="row g-5 align-items-start">

        {/* ----- Left: Image Carousel ----- */}
        <div className="col-md-6">

          <div
            style={{
              background: '#f4f4f4',
              borderRadius: '2px',
              overflow: 'hidden',
              aspectRatio: '1 / 1',
              position: 'relative',
              userSelect: 'none',
              cursor: isZoomed ? 'zoom-out' : 'zoom-in',
            }}
            onClick={() => setIsZoomed(z => !z)}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => {
              if (zoomImgRef.current) zoomImgRef.current.style.transformOrigin = '50% 50%';
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <img
              ref={zoomImgRef}
              src={images[activeIdx] || PLACEHOLDER_LG}
              alt={product.name}
              className="w-100 h-100"
              style={{
                objectFit: 'cover',
                display: 'block',
                transform: isZoomed ? 'scale(2)' : 'scale(1)',
                transition: 'transform 0.25s ease',
                transformOrigin: '50% 50%',
              }}
              onError={e => { e.target.src = PLACEHOLDER_LG; }}
            />

            {images.length > 1 && (
              <>
                <button onClick={e => { e.stopPropagation(); goPrev(); }} aria-label="Previous image"
                  style={{ ...arrowBtnStyle, left: '10px' }}>‹</button>
                <button onClick={e => { e.stopPropagation(); goNext(); }} aria-label="Next image"
                  style={{ ...arrowBtnStyle, right: '10px' }}>›</button>

                <div style={{ position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px', zIndex: 2 }}>
                  {images.map((_, i) => (
                    <button key={i} onClick={e => { e.stopPropagation(); setActiveIdx(i); }} aria-label={`Image ${i + 1}`}
                      style={{ width: i === activeIdx ? '20px' : '8px', height: '8px', borderRadius: '4px',
                        background: i === activeIdx ? '#111' : 'rgba(0,0,0,0.25)', border: 'none', padding: 0,
                        cursor: 'pointer', transition: 'all 0.2s ease' }} />
                  ))}
                </div>
              </>
            )}
          </div>

          {images.length > 1 && (
            <div className="d-flex gap-2 mt-2 flex-wrap">
              {images.map((img, i) => (
                <button key={i} onClick={() => { setActiveIdx(i); setIsZoomed(false); }} aria-label={`View image ${i + 1}`}
                  style={{ width: '64px', height: '64px', borderRadius: '2px', overflow: 'hidden',
                    border: i === activeIdx ? '2px solid #111' : '1px solid #ddd', padding: 0,
                    cursor: 'pointer', background: '#f4f4f4', flexShrink: 0, transition: 'border-color 0.15s ease' }}>
                  <img src={img} alt={`View ${i + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={e => { e.target.src = PLACEHOLDER_SM; }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ----- Right: Info ----- */}
        <div className="col-md-6">

          <h2 className="fw-bold mb-1" style={{ letterSpacing: '0.04em', lineHeight: 1.2 }}>
            {product.name}
          </h2>

          <p className="fw-bold mb-3" style={{ fontSize: '1.5rem', color: '#111' }}>
            ${product.price.toFixed(2)}
          </p>

          <p className="small fw-semibold text-uppercase mb-3" style={{ letterSpacing: '0.08em', color: stockInfo.color }}>
            {stockInfo.text}
          </p>

          <p className="mb-4" style={{ color: '#555', lineHeight: 1.7 }}>
            {product.description}
          </p>

          <hr style={{ borderColor: '#e0e0e0' }} />

          {/* ----- Size Selector (only for hasSize products) ----- */}
          {product.hasSize && (
            <div className="mb-4">
              <p className="small fw-bold text-uppercase mb-2" style={{ letterSpacing: '0.08em' }}>Size</p>
              <div className="d-flex gap-2 flex-wrap">
                {SIZES.map(size => {
                  const sizeQty = getSizeStock(product, size);
                  const oos     = sizeQty === 0;
                  const isSelected = selectedSize === size;
                  return (
                    <button
                      key={size}
                      onClick={() => !oos && handleSizeSelect(size)}
                      disabled={oos}
                      title={oos ? 'Out of stock' : `${sizeQty} available`}
                      style={{
                        width: '48px',
                        height: '48px',
                        position: 'relative',
                        border: isSelected ? '2px solid #111' : sizeError && !oos ? '1px solid #c84b11' : '1px solid #ddd',
                        background: isSelected ? '#111' : oos ? '#f4f4f4' : 'white',
                        color: isSelected ? 'white' : oos ? '#bbb' : '#111',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        cursor: oos ? 'not-allowed' : 'pointer',
                        borderRadius: '2px',
                        transition: 'all 0.15s ease',
                        textDecoration: oos ? 'line-through' : 'none',
                      }}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>

              {sizeError && (
                <p className="small mt-2 mb-0 fw-semibold" style={{ color: '#c84b11' }}>
                  Please select a size before adding to cart.
                </p>
              )}

              {/* Show selected size's stock when low */}
              {selectedSize && getSizeStock(product, selectedSize) <= 5 && getSizeStock(product, selectedSize) > 0 && (
                <p className="small mt-2 mb-0 fw-semibold" style={{ color: '#c84b11' }}>
                  Only {getSizeStock(product, selectedSize)} left in size {selectedSize}
                </p>
              )}
            </div>
          )}

          {/* ----- Quantity Selector ----- */}
          <div className="mb-4">
            <p className="small fw-bold text-uppercase mb-2" style={{ letterSpacing: '0.08em' }}>Quantity</p>
            <div className="d-flex align-items-center" style={{ width: 'fit-content', border: '1px solid #ddd', borderRadius: '2px' }}>
              <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                disabled={isOutOfStock || quantity <= 1}
                style={{ ...qtyBtnStyle, borderRight: '1px solid #ddd', borderRadius: '2px 0 0 2px' }}
              >−</button>
              <span style={{ width: '48px', textAlign: 'center', fontWeight: 600, fontSize: '0.95rem' }}>
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(q => Math.min(Math.max(maxQty, 1), q + 1))}
                disabled={isOutOfStock || quantity >= maxQty}
                style={{ ...qtyBtnStyle, borderLeft: '1px solid #ddd', borderRadius: '0 2px 2px 0' }}
              >+</button>
            </div>
          </div>

          {/* ----- Add to Cart ----- */}
          <button
            className="btn btn-piggy w-100 py-3"
            style={{ fontSize: '1rem', letterSpacing: '0.06em' }}
            onClick={handleAddToCart}
            disabled={isOutOfStock}
          >
            {isOutOfStock ? 'Out of Stock' : added ? '✓ Added to Cart' : 'Add to Cart'}
          </button>

          {added && (
            <div className="mt-3 text-center">
              <Link to="/cart" className="small text-decoration-none" style={{ color: '#c84b11' }}>
                View Cart →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
